const API_BASE_URL = (process.env.REACT_APP_API_URL || '').trim();
const PAYMENTS_API_BASE_URL = (process.env.REACT_APP_PAYMENTS_API_URL || API_BASE_URL || '').trim();
export const SPADES_UPDATED_EVENT = 'polarace:spades-updated';
export const FOLLOW_GRAPH_UPDATED_EVENT = 'polarace:follow-graph-updated';
export const NOTIFICATIONS_UPDATED_EVENT = 'polarace:notifications-updated';
export const AUTH_TOKEN_KEY = 'polarace_token';
export const AUTH_USER_KEY = 'polarace_user';
const AUTH_TOKEN_COOKIE_KEY = 'polarace_token';
const AUTH_TOKEN_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

const LEGACY_AUTH_KEYS = ['spaceace_token', 'spaceace_user', 'spaceace_redirect'];

if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL must be configured for the frontend build');
}

export const clearLegacyAuthStorage = () => {
  LEGACY_AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
};

const getCookieDomain = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const hostname = window.location.hostname.toLowerCase();
  if (hostname === 'polarace.com' || hostname === 'www.polarace.com' || hostname.endsWith('.polarace.com')) {
    return 'domain=.polarace.com; ';
  }

  return '';
};

const readCookieValue = (key) => {
  if (typeof document === 'undefined') {
    return null;
  }

  const prefix = `${encodeURIComponent(key)}=`;
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(prefix));

  if (!cookie) {
    return null;
  }

  const rawValue = cookie.slice(prefix.length);
  try {
    return decodeURIComponent(rawValue);
  } catch {
    return rawValue;
  }
};

const writeCookieValue = (key, value, { maxAgeSeconds } = {}) => {
  if (typeof document === 'undefined') {
    return;
  }

  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'Secure; ' : '';
  const maxAge = Number.isFinite(maxAgeSeconds) ? `max-age=${maxAgeSeconds}; ` : '';
  document.cookie = `${encodeURIComponent(key)}=${encodeURIComponent(value)}; ${getCookieDomain()}path=/; ${maxAge}SameSite=Lax; ${secure}`.trim();
};

const clearCookieValue = (key) => {
  if (typeof document === 'undefined') {
    return;
  }

  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'Secure; ' : '';
  document.cookie = `${encodeURIComponent(key)}=; ${getCookieDomain()}path=/; max-age=0; SameSite=Lax; ${secure}`.trim();
};

export const readStoredToken = () => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    if (!readCookieValue(AUTH_TOKEN_COOKIE_KEY)) {
      writeCookieValue(AUTH_TOKEN_COOKIE_KEY, token, {
        maxAgeSeconds: AUTH_TOKEN_COOKIE_MAX_AGE_SECONDS,
      });
    }
    return token;
  }

  const cookieToken = readCookieValue(AUTH_TOKEN_COOKIE_KEY);
  if (cookieToken) {
    localStorage.setItem(AUTH_TOKEN_KEY, cookieToken);
    return cookieToken;
  }

  return null;
};

export const readStoredUser = () => {
  try {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const writeStoredToken = (token) => {
  if (typeof token === 'string' && token.trim()) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
    writeCookieValue(AUTH_TOKEN_COOKIE_KEY, token, {
      maxAgeSeconds: AUTH_TOKEN_COOKIE_MAX_AGE_SECONDS,
    });
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    clearCookieValue(AUTH_TOKEN_COOKIE_KEY);
  }
  clearLegacyAuthStorage();
};

export const writeStoredUser = (user) => {
  if (user && typeof user === 'object') {
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(AUTH_USER_KEY);
  }
  clearLegacyAuthStorage();
  return user ?? null;
};

export const mergeStoredUser = (updates) => {
  const currentUser = readStoredUser() || {};
  const nextUser = { ...currentUser, ...updates };
  writeStoredUser(nextUser);
  return nextUser;
};

export const clearStoredAuth = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
  clearCookieValue(AUTH_TOKEN_COOKIE_KEY);
  clearLegacyAuthStorage();
};

const broadcastSpadesBalance = (spadeBalance) => {
  if (typeof spadeBalance !== 'number' || Number.isNaN(spadeBalance)) {
    return;
  }

  mergeStoredUser({ spadesBalance: spadeBalance });

  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent(SPADES_UPDATED_EVENT, {
        detail: { spadeBalance },
      })
    );
  }
};

const broadcastFollowGraphUpdate = ({ targetUsername, isFollowing, followersCount }) => {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent(FOLLOW_GRAPH_UPDATED_EVENT, {
        detail: {
          actorUsername: readStoredUser()?.username || null,
          targetUsername: typeof targetUsername === 'string' ? targetUsername : null,
          isFollowing: Boolean(isFollowing),
          followersCount: typeof followersCount === 'number' ? followersCount : null,
        },
      })
    );
  }
};

const broadcastNotificationsUpdated = (unreadCount) => {
  if (typeof unreadCount !== 'number' || Number.isNaN(unreadCount)) {
    return;
  }

  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(
      new CustomEvent(NOTIFICATIONS_UPDATED_EVENT, {
        detail: { unreadCount },
      })
    );
  }
};

const resolveStoredUserId = (userId) => userId || readStoredUser()?.id || null;

const mapPaymentTransactionType = (type) => {
  if (typeof type !== 'string') {
    return type;
  }
  if (type === 'submission_donation_refund') {
    return 'refund';
  }
  if (type.endsWith('_donation_sent')) {
    return 'spend';
  }
  if (type.endsWith('_donation_received')) {
    return 'earning';
  }
  if (type === 'message_hold') {
    return 'message_sent';
  }
  if (type === 'message_release' || type === 'message_received') {
    return 'message_received';
  }
  if (type === 'message_refund') {
    return 'message_refund';
  }
  return type;
};

const authHeaders = (token = readStoredToken(), includeJson = false) => {
  const headers = {};
  if (includeJson) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const parseResponse = async (res, fallbackMessage) => {
  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok) {
    throw new Error(data.detail || fallbackMessage);
  }

  return data;
};

export const authApi = {
  getTwitchAuthUrl: () => `${API_BASE_URL}/login/twitch`,
  getGoogleAuthUrl: () => `${API_BASE_URL}/login/google`,

  getCurrentUser: async (token) => {
    const res = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: authHeaders(token),
    });
    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }

    if (!res.ok) {
      const error = new Error(data.detail || 'Invalid token');
      error.status = res.status;
      throw error;
    }

    return data;
  },

  approveObsDeviceCode: async (userCode, token = readStoredToken()) => {
    const normalizedUserCode = typeof userCode === 'string' ? userCode.trim().toUpperCase() : '';
    if (!normalizedUserCode) {
      throw new Error('OBS user code is required');
    }

    const res = await fetch(`${API_BASE_URL}/auth/obs/device/approve`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ userCode: normalizedUserCode }),
    });
    return parseResponse(res, 'Failed to approve OBS device code');
  },

  completeRegistration: async (token, { username, dateOfBirth, country, firstName, lastName }) => {
    const res = await fetch(`${API_BASE_URL}/auth/complete-registration`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ username, dateOfBirth, country, firstName, lastName }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Registration failed');
    return data;
  },

  checkUsername: async (username) => {
    const res = await fetch(
      `${API_BASE_URL}/auth/check-username/${encodeURIComponent(username)}`
    );
    return parseResponse(res, 'Failed to check username');
  },

  getUserByUsername: async (username, token = readStoredToken()) => {
    const trimmedUsername = typeof username === 'string' ? username.trim() : '';
    if (!trimmedUsername) {
      throw new Error('Streamer username is required');
    }

    const res = await fetch(
      `${API_BASE_URL}/users/by-username/${encodeURIComponent(trimmedUsername)}`,
      {
        headers: authHeaders(token),
      }
    );
    return parseResponse(res, 'Failed to look up streamer username');
  },

  searchUsers: async (query, token = readStoredToken()) => {
    const trimmedQuery = typeof query === 'string' ? query.trim() : '';
    if (trimmedQuery.length < 2) {
      return [];
    }

    const res = await fetch(
      `${API_BASE_URL}/users/search?query=${encodeURIComponent(trimmedQuery)}`,
      {
        headers: authHeaders(token),
      }
    );
    return parseResponse(res, 'Failed to search users');
  },

  getNotifications: async ({ limit = 30 } = {}, token = readStoredToken()) => {
    const safeLimit = Math.min(Math.max(Number(limit) || 30, 1), 100);
    const res = await fetch(`${API_BASE_URL}/notifications?limit=${safeLimit}`, {
      headers: authHeaders(token),
    });
    return parseResponse(res, 'Failed to load notifications');
  },

  getNotificationUnreadCount: async (token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/notifications/unread-count`, {
      headers: authHeaders(token),
    });
    const data = await parseResponse(res, 'Failed to load notification count');
    const unreadCount = Number(data.unreadCount || 0);
    broadcastNotificationsUpdated(unreadCount);
    return { unreadCount };
  },

  markNotificationRead: async (notificationId, token = readStoredToken()) => {
    const normalizedId = typeof notificationId === 'string' ? notificationId.trim() : '';
    if (!normalizedId) {
      throw new Error('Notification ID is required');
    }

    const res = await fetch(
      `${API_BASE_URL}/notifications/${encodeURIComponent(normalizedId)}/read`,
      {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify({}),
      }
    );
    const data = await parseResponse(res, 'Failed to mark notification read');
    const unreadCount = Number(data.unreadCount || 0);
    broadcastNotificationsUpdated(unreadCount);
    return { ...data, unreadCount };
  },

  markAllNotificationsRead: async (token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/notifications/read-all`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({}),
    });
    const data = await parseResponse(res, 'Failed to mark notifications read');
    const unreadCount = Number(data.unreadCount || 0);
    broadcastNotificationsUpdated(unreadCount);
    return { ...data, unreadCount };
  },

  clearNotifications: async (token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/notifications`, {
      method: 'DELETE',
      headers: authHeaders(token),
    });
    const data = await parseResponse(res, 'Failed to clear notifications');
    const unreadCount = Number(data.unreadCount || 0);
    broadcastNotificationsUpdated(unreadCount);
    return { ...data, unreadCount };
  },

  getPublicProfile: async (username) => {
    const trimmedUsername = typeof username === 'string' ? username.trim() : '';
    if (!trimmedUsername) {
      throw new Error('Username is required');
    }

    const res = await fetch(
      `${API_BASE_URL}/profile/${encodeURIComponent(trimmedUsername)}`
    );
    return parseResponse(res, 'Failed to load public profile');
  },

  getFollowersList: async (username) => {
    const trimmedUsername = typeof username === 'string' ? username.trim() : '';
    if (!trimmedUsername) {
      throw new Error('Username is required');
    }

    const res = await fetch(
      `${API_BASE_URL}/profile/${encodeURIComponent(trimmedUsername)}/followers`
    );
    return parseResponse(res, 'Failed to load followers');
  },

  getFollowingList: async (username) => {
    const trimmedUsername = typeof username === 'string' ? username.trim() : '';
    if (!trimmedUsername) {
      throw new Error('Username is required');
    }

    const res = await fetch(
      `${API_BASE_URL}/profile/${encodeURIComponent(trimmedUsername)}/following`
    );
    return parseResponse(res, 'Failed to load following');
  },

  getFollowersCount: async (username) => {
    const trimmedUsername = typeof username === 'string' ? username.trim() : '';
    if (!trimmedUsername) {
      throw new Error('Username is required');
    }

    const res = await fetch(
      `${API_BASE_URL}/profile/${encodeURIComponent(trimmedUsername)}/followers/count`
    );
    return parseResponse(res, 'Failed to load followers count');
  },

  checkIsFollowing: async (username, token = readStoredToken()) => {
    const trimmedUsername = typeof username === 'string' ? username.trim() : '';
    if (!trimmedUsername) {
      throw new Error('Username is required');
    }

    const res = await fetch(
      `${API_BASE_URL}/profile/${encodeURIComponent(trimmedUsername)}/is-following`,
      { headers: authHeaders(token) }
    );
    return parseResponse(res, 'Failed to check follow status');
  },

  followUser: async (username, token = readStoredToken()) => {
    const trimmedUsername = typeof username === 'string' ? username.trim() : '';
    if (!trimmedUsername) {
      throw new Error('Username is required');
    }

    const res = await fetch(
      `${API_BASE_URL}/profile/${encodeURIComponent(trimmedUsername)}/follow`,
      {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify({}),
      }
    );
    const data = await parseResponse(res, 'Failed to follow user');
    broadcastFollowGraphUpdate({
      targetUsername: trimmedUsername,
      isFollowing: data.isFollowing,
      followersCount: data.followersCount,
    });
    return data;
  },

  unfollowUser: async (username, token = readStoredToken()) => {
    const trimmedUsername = typeof username === 'string' ? username.trim() : '';
    if (!trimmedUsername) {
      throw new Error('Username is required');
    }

    const res = await fetch(
      `${API_BASE_URL}/profile/${encodeURIComponent(trimmedUsername)}/follow`,
      {
        method: 'DELETE',
        headers: authHeaders(token),
      }
    );
    const data = await parseResponse(res, 'Failed to unfollow user');
    broadcastFollowGraphUpdate({
      targetUsername: trimmedUsername,
      isFollowing: data.isFollowing,
      followersCount: data.followersCount,
    });
    return data;
  },

  logout: async (token = readStoredToken()) => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: authHeaders(token),
      });
    } catch {
      // Ignore network errors on logout
    }
  },

  getProfile: async (token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/profile/me`, {
      headers: authHeaders(token),
    });
    return parseResponse(res, 'Failed to load profile');
  },

  updateProfile: async (data, token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/profile/me`, {
      method: 'PUT',
      headers: authHeaders(token, true),
      body: JSON.stringify(data),
    });
    return parseResponse(res, 'Failed to update profile');
  },

  addPlatform: async (data, token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/profile/platforms`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify(data),
    });
    return parseResponse(res, 'Failed to add platform');
  },

  removePlatform: async (platform, token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/profile/platforms/${encodeURIComponent(platform)}`, {
      method: 'DELETE',
      headers: authHeaders(token),
    });
    return parseResponse(res, 'Failed to remove platform');
  },

  startPlatformConnect: async (platform, token = readStoredToken()) => {
    const normalizedPlatform = typeof platform === 'string' ? platform.trim().toLowerCase() : '';
    if (!normalizedPlatform) {
      throw new Error('Platform is required');
    }

    const res = await fetch(
      `${API_BASE_URL}/profile/platforms/connect/${encodeURIComponent(normalizedPlatform)}`,
      {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify({}),
      }
    );
    return parseResponse(res, 'Failed to start platform connection');
  },

  reconcileTwitchEventSub: async (token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/auth/twitch/eventsub/reconcile`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({}),
    });
    return parseResponse(res, 'Failed to reconcile Twitch live sync');
  },

  getIntroVideo: async (token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/profile/intro-video`, {
      headers: authHeaders(token),
    });
    return parseResponse(res, 'Failed to load intro video');
  },

  createMediaUploadUrl: async ({ kind, filename, contentType }, token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/profile/media/upload-url`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ kind, filename, contentType }),
    });
    return parseResponse(res, 'Failed to create media upload URL');
  },

  uploadFileToPresignedUrl: async (uploadUrl, file, contentType = file?.type) => {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType || 'application/octet-stream',
      },
      body: file,
    });

    if (!res.ok) {
      throw new Error('Failed to upload media file');
    }
  },

  uploadProfileMediaDirect: async ({ kind, file }, token = readStoredToken()) => {
    if (!file) {
      throw new Error('Media file is required');
    }

    const formData = new FormData();
    formData.append('kind', kind);
    formData.append('file', file);

    let res;
    try {
      res = await fetch(`${API_BASE_URL}/profile/media/upload-url`, {
        method: 'POST',
        headers: authHeaders(token),
        body: formData,
      });
    } catch {
      const error = new Error('Could not reach the profile media upload service.');
      error.status = 0;
      throw error;
    }

    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }

    if (!res.ok) {
      const error = new Error(data.detail || 'Failed to upload media file');
      error.status = res.status;
      throw error;
    }

    return data;
  },

  uploadProfileMedia: async ({ kind, file }, token = readStoredToken()) => {
    const supportsDirectUpload = kind === 'avatar' || kind === 'banner';

    if (supportsDirectUpload) {
      try {
        return await authApi.uploadProfileMediaDirect({ kind, file }, token);
      } catch (error) {
        const status = typeof error?.status === 'number' ? error.status : null;
        const shouldFallback =
          status === null
          || status === 0
          || status >= 500
          || status === 404
          || status === 405;

        if (!shouldFallback) {
          throw error;
        }

        console.warn('Direct profile media upload failed; falling back to presigned upload.', error);
      }
    }

    const uploadData = await authApi.createMediaUploadUrl(
      {
        kind,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
      },
      token
    );

    await authApi.uploadFileToPresignedUrl(
      uploadData.uploadUrl,
      file,
      file.type || 'application/octet-stream'
    );

    return uploadData;
  },

  updateIntroVideo: async ({ s3Link, processingStatus }, token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/profile/intro-video`, {
      method: 'PUT',
      headers: authHeaders(token, true),
      body: JSON.stringify({ s3Link, processingStatus }),
    });
    return parseResponse(res, 'Failed to update intro video');
  },

  getPaymentBalance: async (userId = resolveStoredUserId(), token = readStoredToken()) => {
    if (!userId) {
      throw new Error('Missing user id');
    }

    const res = await fetch(
      `${PAYMENTS_API_BASE_URL}/payments/balance/${encodeURIComponent(userId)}`,
      { headers: authHeaders(token) }
    );
    const data = await parseResponse(res, 'Failed to load spades balance');
    broadcastSpadesBalance(data.spade_balance);
    return data;
  },

  getPaymentLedger: async (userId = resolveStoredUserId(), token = readStoredToken()) => {
    if (!userId) {
      throw new Error('Missing user id');
    }

    const res = await fetch(
      `${PAYMENTS_API_BASE_URL}/payments/ledger/${encodeURIComponent(userId)}`,
      { headers: authHeaders(token) }
    );
    const data = await parseResponse(res, 'Failed to load payment history');
    return Array.isArray(data)
      ? data.map((entry) => ({
          ...entry,
          type: mapPaymentTransactionType(entry.type),
        }))
      : [];
  },

  getAdminPlatformRevenue: async (token = readStoredToken()) => {
    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/admin/platform-revenue`, {
      headers: authHeaders(token),
    });
    return parseResponse(res, 'Failed to load platform revenue');
  },

  grantAdminSpades: async (userId, spadeAmount, token = readStoredToken()) => {
    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/admin/grant`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ user_id: userId, spade_amount: spadeAmount }),
    });
    const data = await parseResponse(res, 'Failed to grant spades');
    if (userId === resolveStoredUserId()) {
      broadcastSpadesBalance(data.spade_balance);
    }
    return data;
  },

  getAdminUsers: async (token = readStoredToken()) => {
    const res = await fetch(`${API_BASE_URL}/users/admin/users`, {
      headers: authHeaders(token),
    });
    return parseResponse(res, 'Failed to load admin users');
  },

  purchaseSpades: async (spadeAmount, token = readStoredToken()) => {
    const purchaseBody = { spade_amount: spadeAmount };

    try {
      const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/buy-spades`, {
        method: 'POST',
        headers: authHeaders(token, true),
        body: JSON.stringify(purchaseBody),
      });
      const data = await parseResponse(res, 'Failed to start checkout');

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return { ...data, mode: 'checkout' };
      }

      return data;
    } catch (checkoutError) {
      try {
        const demoRes = await fetch(`${PAYMENTS_API_BASE_URL}/payments/buy-spades/complete`, {
          method: 'POST',
          headers: authHeaders(token, true),
          body: JSON.stringify(purchaseBody),
        });
        const demoData = await parseResponse(demoRes, 'Failed to complete demo purchase');
        broadcastSpadesBalance(demoData.spade_balance);
        return { ...demoData, mode: 'demo' };
      } catch {
        throw checkoutError;
      }
    }
  },

  giftSpades: async ({ streamerId, amount }, token = readStoredToken()) => {
    const normalizedStreamerId = typeof streamerId === 'string' ? streamerId.trim() : '';
    const normalizedAmount = Number(amount);
    if (!normalizedStreamerId) {
      throw new Error('Missing streamer id');
    }
    if (!Number.isFinite(normalizedAmount) || normalizedAmount < 1) {
      throw new Error('Amount must be at least 1 spade');
    }

    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/gift`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({
        streamer_id: normalizedStreamerId,
        amount: Math.floor(normalizedAmount),
      }),
    });
    const data = await parseResponse(res, 'Failed to gift spades');
    broadcastSpadesBalance(data.new_balance);
    return data;
  },

  verifyStripeSession: async (sessionId, token = readStoredToken()) => {
    const normalizedSessionId = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!normalizedSessionId) {
      throw new Error('Missing Stripe session id');
    }

    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/verify-session`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ session_id: normalizedSessionId }),
    });
    const data = await parseResponse(res, 'Failed to verify Stripe session');
    broadcastSpadesBalance(data.spade_balance);
    return data;
  },

  getConnectStatus: async (userId = resolveStoredUserId(), token = readStoredToken()) => {
    if (!userId) {
      throw new Error('Missing user id');
    }

    const res = await fetch(
      `${PAYMENTS_API_BASE_URL}/payments/connect/status/${encodeURIComponent(userId)}`,
      { headers: authHeaders(token) }
    );
    return parseResponse(res, 'Failed to load bank connection status');
  },

  startConnectOnboard: async (token = readStoredToken()) => {
    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/connect/onboard`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({}),
    });
    return parseResponse(res, 'Failed to start bank onboarding');
  },

  cashoutSpades: async (spadeAmount, token = readStoredToken()) => {
    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/cashout`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ spade_amount: spadeAmount }),
    });
    const data = await parseResponse(res, 'Failed to cash out spades');
    broadcastSpadesBalance(data.new_balance);
    return data;
  },

  getPlatformPlanStatus: async (token = readStoredToken()) => {
    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/platform/status`, {
      headers: authHeaders(token),
    });
    return parseResponse(res, 'Failed to load Discovery Plan status');
  },

  startPlatformPlanSubscribe: async (token = readStoredToken()) => {
    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/platform/subscribe`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({}),
    });
    return parseResponse(res, 'Failed to start Discovery Plan checkout');
  },

  activatePlatformPlan: async (sessionId, token = readStoredToken()) => {
    const normalized = typeof sessionId === 'string' ? sessionId.trim() : '';
    if (!normalized) {
      throw new Error('Missing Stripe session id');
    }
    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/platform/activate`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ session_id: normalized }),
    });
    return parseResponse(res, 'Failed to activate Discovery Plan');
  },

  cancelPlatformPlan: async (token = readStoredToken()) => {
    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/platform/cancel`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({}),
    });
    return parseResponse(res, 'Failed to cancel Discovery Plan');
  },

  reactivatePlatformPlan: async (token = readStoredToken()) => {
    const res = await fetch(`${PAYMENTS_API_BASE_URL}/payments/platform/reactivate`, {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({}),
    });
    return parseResponse(res, 'Failed to reactivate Discovery Plan');
  },

  getCashoutHistory: async (userId = resolveStoredUserId(), token = readStoredToken()) => {
    if (!userId) {
      throw new Error('Missing user id');
    }

    const res = await fetch(
      `${PAYMENTS_API_BASE_URL}/payments/cashout/history/${encodeURIComponent(userId)}`,
      { headers: authHeaders(token) }
    );
    return parseResponse(res, 'Failed to load cashout history');
  },

  // ── Email Auth ──────────────────────────────────────────

  checkEmail: async (email) => {
    const res = await fetch(
      `${API_BASE_URL}/auth/email/check?email=${encodeURIComponent(email)}`
    );
    const data = await parseResponse(res, 'Failed to check email');
    return data.exists;
  },

  sendEmailCode: async (email) => {
    const res = await fetch(`${API_BASE_URL}/auth/email/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    return parseResponse(res, 'Failed to send code');
  },

  verifyEmailCode: async (email, code) => {
    const res = await fetch(`${API_BASE_URL}/auth/email/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code }),
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Verification failed');
    }
    return res.json();
  },
};
